// src/context/CartContextCliente.jsx
import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

const itemKey = (id, talle) => `${id}__${(talle || "").trim().toUpperCase()}`;

export function CartProvider({ children, storageKey = "cart_default" }) {
  const [cart, setCart] = useState([]);
  const [isHydrated, setIsHydrated] = useState(false);

  // ✅ Cargar carrito cuando cambia la empresa
  useEffect(() => {
    setIsHydrated(false);

    try {
      const savedCart = localStorage.getItem(storageKey);
      const parsed = savedCart ? JSON.parse(savedCart) : [];
      const safeCart = Array.isArray(parsed) ? parsed : [];

      setCart(safeCart);
      console.log("Cargando carrito desde:", storageKey, safeCart);
    } catch (error) {
      console.error("Error leyendo localStorage:", error);
      setCart([]);
    } finally {
      setIsHydrated(true);
    }
  }, [storageKey]);

  // ✅ Guardar carrito SOLO después de hidratar
  useEffect(() => {
    if (!isHydrated) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(cart));
      console.log("Guardando carrito en:", storageKey, cart);
    } catch (error) {
      console.error("Error guardando localStorage:", error);
    }
  }, [cart, storageKey, isHydrated]);

  const addToCart = (producto) => {
    const id = producto?.id;
    const talle = (producto?.talle || "").trim();
    const cantidad = Number(producto?.cantidad || 1);

    if (!id) return;

    setCart((prev) => {
      const k = itemKey(id, talle);
      const existing = prev.find((it) => itemKey(it.id, it.talle) === k);

      if (existing) {
        return prev.map((it) =>
          itemKey(it.id, it.talle) === k
            ? { ...it, cantidad: Number(it.cantidad || 0) + cantidad }
            : it
        );
      }

      return [
        ...prev,
        {
          ...producto,
          talle: talle || undefined,
          cantidad: cantidad > 0 ? cantidad : 1,
        },
      ];
    });
  };

  const incrementItem = (producto) => {
    const id = producto?.id;
    const talle = (producto?.talle || "").trim();

    if (!id) return;

    setCart((prev) => {
      const k = itemKey(id, talle);
      const existing = prev.find((it) => itemKey(it.id, it.talle) === k);

      if (!existing) {
        return [
          ...prev,
          {
            ...producto,
            talle: talle || undefined,
            cantidad: 1,
          },
        ];
      }

      return prev.map((it) =>
        itemKey(it.id, it.talle) === k
          ? { ...it, cantidad: Number(it.cantidad || 0) + 1 }
          : it
      );
    });
  };

  const decrementItem = (producto) => {
    const id = producto?.id;
    const talle = (producto?.talle || "").trim();

    if (!id) return;

    setCart((prev) => {
      const k = itemKey(id, talle);
      const existing = prev.find((it) => itemKey(it.id, it.talle) === k);
      if (!existing) return prev;

      const newQty = Number(existing.cantidad || 0) - 1;

      if (newQty <= 0) {
        return prev.filter((it) => itemKey(it.id, it.talle) !== k);
      }

      return prev.map((it) =>
        itemKey(it.id, it.talle) === k ? { ...it, cantidad: newQty } : it
      );
    });
  };

  const removeFromCart = (id, talle) => {
    if (!id) return;

    setCart((prev) => {
      if (typeof talle === "string") {
        const k = itemKey(id, talle);
        return prev.filter((it) => itemKey(it.id, it.talle) !== k);
      }

      return prev.filter((it) => it.id !== id);
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(storageKey);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        incrementItem,
        decrementItem,
        removeFromCart,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}